/* eslint-disable no-shadow */
import { createAction, getType, ActionType } from 'typesafe-actions';

import { RootState } from 'reducers/rootReducer';
import { gridConfig } from 'config/gridConfig';
import { Precision } from 'types/precision';
import { Setting } from 'types/settings';

export interface SettingsState {
  readonly gridLines: string;
  gridSizeInFeet: number;
  readonly precision: Precision;
  readonly unitOfMeasurement: string;
  readonly settingsModalForm: any;
  readonly lastUpdatedSetting: Setting;
  readonly exteriorWallDesktop: string;
  readonly exteriorWallMobile: string;
  readonly interiorWallDesktop: string;
  readonly interiorWallMobile: string;
  readonly zoomSpeed: number;
}

// Action Creators
export const actions = {
  switchGridShow: createAction('settings/switchGridShow')<string>(),
  switchPrecision: createAction('settings/switchPrecision')<number>(),
  switchUnitOfMeasurement: createAction('settings/switchUnitOfMeasurement')<string>(),
  setGridSize: createAction('settings/gridSizeInFeet')<number>(),
  setZoomSpeed: createAction('settings/setZoomSpeed')<number>(),
  setUserSettings: createAction('settings/setUserSettings')<Record<string, string | boolean | number>>(),
  setSettingsModalForm: createAction('settings/setSettingsModalForm')<any>(),
  updateLastSetting: createAction('settings/updateLastSetting')<Record<string, string | boolean | number>>(),
  switchExteriorDesktop: createAction('settings/switchExteriorDesktop')<string>(),
  switchExteriorMobile: createAction('settings/switchExteriorMobile')<string>(),
  switchInteriorDesktop: createAction('settings/switchInteriorDesktop')<string>(),
  switchInteriorMobile: createAction('settings/switchInteriorMobile')<string>(),
};

export type Actions = ActionType<typeof actions>;

// Initial State
const getStoredZoomSpeed = (): number => {
  try {
    if (typeof localStorage === 'undefined') return 1;
    const stored = Number(localStorage.getItem('zoomSpeed'));
    return Number.isFinite(stored) && stored > 0 ? stored : 1;
  } catch (err) {
    return 1;
  }
};

const initialState: SettingsState = {
  gridLines: gridConfig.defaultGridLines,
  precision: gridConfig.defaultPrecision,
  unitOfMeasurement: gridConfig.defaultUnitOfMeasurement,
  gridSizeInFeet: gridConfig.gridSizeInFeet,
  settingsModalForm: [],
  lastUpdatedSetting: { uuid: '', value: undefined },
  exteriorWallDesktop: gridConfig.defaultExteriorWalldesktop,
  exteriorWallMobile: gridConfig.defaultExteriorWallMobile,
  interiorWallDesktop: gridConfig.defaultInteriorWalldesktop,
  interiorWallMobile: gridConfig.defaultInteriorWallmobile,
  zoomSpeed: getStoredZoomSpeed(),
};

// Selectors
const getSettings = (rootState: RootState): SettingsState => rootState.settings;

const gridLines = (rootState: RootState): string => getSettings(rootState).gridLines;

const getPrecision = (rootState: RootState): Precision => getSettings(rootState).precision;

const getUnitOfMeasure = (rootState: RootState): string => getSettings(rootState).unitOfMeasurement;

const getGridSizeInFeet = (rootState: RootState): number => getSettings(rootState).gridSizeInFeet;

const getSettingsModalForm = (rootState: RootState): number => getSettings(rootState).settingsModalForm;

const getLastUpdatedSetting = (rootState: RootState): Setting => getSettings(rootState).lastUpdatedSetting;

const getExteriorDesktop = (rootState: RootState): string => getSettings(rootState).exteriorWallDesktop;

const getExteriorMobile = (rootState: RootState): string => getSettings(rootState).exteriorWallMobile;

const getInteriorDesktop = (rootState: RootState): string => getSettings(rootState).interiorWallDesktop;

const getInteriorMobile = (rootState: RootState): string => getSettings(rootState).interiorWallMobile;

const getZoomSpeed = (rootState: RootState): number => getSettings(rootState).zoomSpeed;



export const selectors = {
  getPrecision,
  gridLines,
  getUnitOfMeasure,
  getGridSizeInFeet,
  getZoomSpeed,
  getSettingsModalForm,
  getLastUpdatedSetting,
  getExteriorDesktop,
  getExteriorMobile,
  getInteriorDesktop,
  getInteriorMobile,
};

// Reducers
const switchGridShowReducer = (state: SettingsState, gridLines: string): SettingsState => ({
  ...state,
  gridLines,
});

const switchPrecisionReducer = (state: SettingsState, precision: Precision): SettingsState => ({
  ...state,
  precision,
});

const switchUnitOfMeasureReducer = (state: SettingsState, unitOfMeasurement: string): SettingsState => ({
  ...state,
  unitOfMeasurement,
});

const setDesktopReducer = (state: SettingsState, exteriorWallDesktop: string): SettingsState => ({
  ...state,
  exteriorWallDesktop,
});

const setMobileReducer = (state: SettingsState, exteriorWallMobile: string): SettingsState => ({
  ...state,
  exteriorWallMobile,
});

const setInteriorDesktopReducer = (state: SettingsState, interiorWallDesktop: string): SettingsState => ({
  ...state,
  interiorWallDesktop,
});

const setInteriorMobileReducer = (state: SettingsState, interiorWallMobile: string): SettingsState => ({
  ...state,
  interiorWallMobile,
});

const setZoomSpeedReducer = (state: SettingsState, zoomSpeed: number): SettingsState => ({
  ...state,
  zoomSpeed,
});


const setGridSizeInFeetReducer = (state: SettingsState, gridSizeInFeet: number): SettingsState => ({
  ...state,
  gridSizeInFeet,
});

const setUserSettingsReducer = (state: SettingsState, settings: any): SettingsState => ({
  ...state,
  ...settings,
});

const setSettingsModalFormReducer = (state: SettingsState, settingsModalForm: any): SettingsState => ({
  ...state,
  settingsModalForm,
});

const setLastUpdatedSetting = (state: SettingsState, lastUpdatedSetting: any): SettingsState => ({
  ...state,
  lastUpdatedSetting,
});

export const reducer = (state: SettingsState = initialState, action: Actions): SettingsState => {
  switch (action.type) {
    case getType(actions.switchGridShow):
      return switchGridShowReducer(state, action.payload);

    case getType(actions.switchPrecision):
      return switchPrecisionReducer(state, action.payload);

    case getType(actions.switchUnitOfMeasurement):
      return switchUnitOfMeasureReducer(state, action.payload);

    case getType(actions.setGridSize):
      return setGridSizeInFeetReducer(state, action.payload);

    case getType(actions.setUserSettings):
      return setUserSettingsReducer(state, action.payload);

    case getType(actions.setSettingsModalForm):
      return setSettingsModalFormReducer(state, action.payload);

    case getType(actions.updateLastSetting):
      return setLastUpdatedSetting(state, action.payload);

    case getType(actions.switchExteriorDesktop):
      return setDesktopReducer(state, action.payload);

    case getType(actions.switchExteriorMobile):
      return setMobileReducer(state, action.payload);

    case getType(actions.switchInteriorDesktop):
      return setInteriorDesktopReducer(state, action.payload);

    case getType(actions.switchInteriorMobile):
      return setInteriorMobileReducer(state, action.payload);

    case getType(actions.setZoomSpeed):
      return setZoomSpeedReducer(state, action.payload);

    default:
      return state;
  }
};
